import type { EamEntra, EamRealm, EamUser } from '../types/api'
import { isObjectId, normaliseObjectId } from './eam'
import type { GraphCall, GraphReply } from './entraSignIn'

export type RegisterGate = 'none' | 'moving' | 'method_id' | 'ready'
export type RegisterOutcome =
  | 'registered_now'
  | 'registered_already'
  | 'other_tenant'
  | 'no_role'
  | 'not_found'
  | 'failed'
  | 'not_run'
export interface RegisterRow { user: EamUser; outcome: RegisterOutcome; detail: string | null }
export interface RegisterRun { rows: RegisterRow[]; stop: null | 'no_role' | 'bad_method_id'; stopDetail: string | null }

const DEFAULT_WAIT_S = 10
const MAX_RETRIES = 3

export function registerGate(realm: EamRealm, entra: EamEntra, users: readonly EamUser[] | null): RegisterGate {
  const sia = realm.sign_in_app
  if (!sia || realm.phone_source !== 'portal') return 'none'
  if (sia.moving_to) return sia.moving_to.app === 'mideye' ? 'moving' : 'none'
  // A missing method_id key means a backend that predates step 2.
  if (sia.app !== 'mideye' || !sia.app_id || !('method_id' in entra) || !users || users.length === 0) return 'none'
  return entra.method_id == null ? 'method_id' : 'ready'
}

export function normaliseMethodId(text: string): string | null {
  const id = normaliseObjectId(text)
  return isObjectId(id) ? id : null
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// A fetch Response body can be read once, so read it here and only here.
async function graphMessage(reply: GraphReply): Promise<string | null> {
  try {
    const body = (await reply.json()) as { error?: { message?: unknown } } | null
    const message = body?.error?.message
    return typeof message === 'string' ? message : null
  } catch {
    return null
  }
}

async function failure(reply: GraphReply): Promise<{ message: string | null; detail: string }> {
  const message = await graphMessage(reply)
  return { message, detail: message ? `${reply.status} ${message}` : String(reply.status) }
}

function retryAfterMs(reply: GraphReply): number {
  const header = reply.headers.get('Retry-After')
  const seconds = header !== null && /^\d+$/.test(header.trim()) ? Number(header.trim()) : DEFAULT_WAIT_S
  return seconds * 1000
}

export async function registerUsers(
  users: readonly EamUser[],
  methodId: string,
  graph: GraphCall,
  opts: { sleep?: (ms: number) => Promise<void>; onRow?: (row: RegisterRow, index: number) => void } = {},
): Promise<RegisterRun> {
  const sleep = opts.sleep ?? defaultSleep
  const wanted = methodId.toLowerCase()
  const rows: RegisterRow[] = []
  let stop: RegisterRun['stop'] = null
  let stopDetail: string | null = null
  let posted = false
  let called = 0
  let noRole = 0

  const push = (user: EamUser, outcome: RegisterOutcome, detail: string | null = null) => {
    const row = { user, outcome, detail }
    rows.push(row)
    opts.onRow?.(row, rows.length - 1)
  }

  const send = async (method: 'GET' | 'POST', path: string, body?: unknown): Promise<GraphReply> => {
    for (let retry = 0; ; retry++) {
      const reply = await graph(method, path, body)
      if (reply.status !== 429 || retry === MAX_RETRIES) return reply
      await sleep(retryAfterMs(reply))
    }
  }

  for (const user of users) {
    if (stop === 'bad_method_id') {
      push(user, 'not_run')
      continue
    }
    if (user.other_tenant) {
      push(user, 'other_tenant')
      continue
    }
    called++
    const path = `/users/${user.object_id}/authentication/externalAuthenticationMethods`
    try {
      const got = await send('GET', path)
      if (got.status === 403) { noRole++; push(user, 'no_role'); continue }
      if (got.status === 404) { push(user, 'not_found'); continue }
      if (got.status !== 200) { push(user, 'failed', (await failure(got)).detail); continue }
      const list = ((await got.json()) as { value?: { configurationId?: unknown }[] } | null)?.value ?? []
      if (list.some((m) => typeof m.configurationId === 'string' && m.configurationId.toLowerCase() === wanted)) {
        push(user, 'registered_already')
        continue
      }
      const first = !posted
      posted = true
      const made = await send('POST', path, { configurationId: wanted, displayName: 'Mideye MFA' })
      if (made.status === 200 || made.status === 201) push(user, 'registered_now')
      else if (made.status === 403) { noRole++; push(user, 'no_role') }
      else if (made.status === 404) push(user, 'not_found')
      else if (made.status === 400 && first) {
        const f = await failure(made)
        stop = 'bad_method_id'
        stopDetail = f.message
        push(user, 'failed', f.detail)
      } else push(user, 'failed', (await failure(made)).detail)
    } catch (e) {
      push(user, 'failed', e instanceof Error ? e.message : String(e))
    }
  }

  if (stop === null && called > 0 && noRole === called) stop = 'no_role'
  return { rows, stop, stopDetail }
}
